import { useNavigate } from 'react-router-dom';
import { formatPrice } from '@/utils';
import { formatUSPhone, computeClientSpend } from '../utils';
import { formatUTCToDateTime } from '@/utils';
import { getAirportTimezone } from '@/utils/airportTimezone';
import StatusBadge from './StatusBadge';
import {
  RiBuildingLine,
  RiMailLine,
  RiPhoneLine,
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiArrowRightLine,
  RiCalendarLine,
  RiTimeLine,
  RiInformationLine,
  RiEditLine,
} from '@remixicon/react';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, ACTIONS, ROLES, normalizeRole } from '@/lib/permissions';

const ClientCard = ({ client }) => {
  const navigate = useNavigate();
  // Edit requires update access on Clients module — hidden for flight_coordinator, finance, compliance_qa.
  const { can, role } = usePermissions();
  const canEditClient = can(MODULES.CLIENTS, ACTIONS.UPDATE);
  // Junior brokers and flight coordinators shouldn't see client spend figures.
  const isJuniorBroker = normalizeRole(role) === ROLES.JUNIOR_BROKER;
  const isFlightCoordinator = normalizeRole(role) === ROLES.FLIGHT_COORDINATOR;
  const name = [client.first_name, client.last_name].filter(Boolean).join(' ') || 'Unknown';
  const company = client.company_name || client.company || '';
  const email = client.email || '';
  const phone = client.contact_numbers?.[0] || client.phone || '';
  const { lifetimeValue, avgFlightSpend } = computeClientSpend(client);
  const latestQuote = client.quotes?.length
    ? [...client.quotes].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
    : null;

  const status = latestQuote?.status;
  const latestFlight = latestQuote?.departure_airport ? (() => {
    const depDate = latestQuote.departure_date ? new Date(latestQuote.departure_date) : null;
    const now = new Date();
    const isCurrentOrPast = depDate && depDate <= now;
    const formattedDate = depDate
      ? formatUTCToDateTime(depDate, getAirportTimezone(latestQuote.departure_airport), "MMM D")
      : null;
    const duration = latestQuote.bids?.[0]?.bid_items?.[0]?.bid_options?.[0]?.flight_time || null;
    return {
      departure_airport: latestQuote.departure_airport,
      arrival_airport: latestQuote.arrival_airport,
      date: formattedDate,
      duration,
      label: isCurrentOrPast ? 'Current Flight' : 'Upcoming Flight',
    };
  })() : null;

  return (
    <div className="bg-white rounded-3xl flex flex-col">
      {/* Header: Name + Status */}
      <div className="p-4 pb-3">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-base font-semibold text-gray-900">{name}</h3>
          <StatusBadge status={status} />
        </div>
        {/* Info rows */}
        <div className="space-y-1">
          {company && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <RiBuildingLine className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span className="truncate">{company}</span>
            </div>
          )}
          {email && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <RiMailLine className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span className="truncate">{email}</span>
            </div>
          )}
          {phone && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <RiPhoneLine className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span>{formatUSPhone(phone)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Financial + Flight row */}
      <div className="px-4 pb-4 flex flex-col sm:flex-row sm:items-end gap-2">
        {!isJuniorBroker && !isFlightCoordinator && (
          <div className={`flex items-center min-w-0 gap-1 ${!latestFlight ? 'w-full' : ''}`}>
            <div className="p-2 bg-gray-50 rounded-lg flex-1">
              <p className="text-md font-semibold text-gray-900">{formatPrice(lifetimeValue)}</p>
              <p className="text-xs text-gray-400">Lifetime Value</p>
            </div>
            <div className="p-2 bg-gray-50 rounded-lg flex-1">
              <p className="text-md font-semibold text-gray-900">{formatPrice(avgFlightSpend)}</p>
              <p className="text-xs text-gray-400">Average Flight Spend</p>
            </div>
          </div>
        )}

        {/* Flight info from latest quote */}
        {latestFlight && (
          <div className="flex-1 flex flex-col">
            <p className="text-xs text-gray-400 uppercase mb-1">{latestFlight.label}</p>
            <div className="flex flex-wrap gap-1.5 text-xs text-gray-600 border-t border-gray-100 pt-2">
              <span className="inline-flex items-center gap-0.5 bg-gray-100 py-1 px-2 rounded-full font-medium">
                <RiFlightTakeoffLine className="w-3 h-3" />
                {latestFlight.departure_airport}
              </span>
              <RiArrowRightLine className="w-4 h-4 text-gray-400 mt-1" />
              <span className="inline-flex items-center gap-0.5 bg-gray-100 py-1 px-2 rounded-full font-medium">
                <RiFlightLandLine className="w-3 h-3" />
                {latestFlight.arrival_airport}
              </span>
              {latestFlight.date && (
                <span className="inline-flex items-center gap-0.5 ml-1">
                  <RiCalendarLine className="w-3 h-3" />
                  {latestFlight.date}
                </span>
              )}
              {latestFlight.duration && (
                <span className="inline-flex items-center gap-0.5">
                  <RiTimeLine className="w-3 h-3" />
                  {latestFlight.duration}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 mx-4 mb-4">
        <button
          onClick={() => navigate(`/clients/${client.client_id || client.id}`, { state: { client } })}
          className="flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium text-primary-500 bg-primary-50/90 transition-colors rounded-full"
        >
          <RiInformationLine className="w-4 h-4" />
          View Details
        </button>
        {canEditClient && (
          <button
            onClick={() => navigate(`/clients/${client.client_id || client.id}/edit`, { state: { client } })}
            className="flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full"
          >
            <RiEditLine className="w-4 h-4" />
            Edit Client
          </button>
        )}
      </div>
    </div>
  );
};

export default ClientCard;